import {
  JourneyDayTripExcursionItem,
  JourneyDayTripExcursionTicketItem,
  JourneyDayTripItem,
} from "../../models/axiosModel"

export const SIC_EXCURSION_PAX = { adultCount: 0, childCount: 0 } as const

export type Step1ExcursionPax = {
  adults: number
  extraBed: number
  children: number
}

export type BuildTicketPairFn = (
  adultCatId: number | undefined,
  adultCount: number,
  childCatId: number | undefined,
  childCount: number,
  existingAdultTix?: JourneyDayTripExcursionTicketItem | null,
  existingChildTix?: JourneyDayTripExcursionTicketItem | null,
) => JourneyDayTripExcursionTicketItem[]

/** Default excursion/add-on ticket counts from Step 1 (adults + extra bed, all children). */
export const defaultExcursionPaxCounts = (pax: Step1ExcursionPax) => ({
  adultCount: Math.max(0, pax.adults) + Math.max(0, pax.extraBed),
  childCount: Math.max(0, pax.children),
})

/** Target pax for non-manual rows on a trip: 0/0 on SIC, Step 1 defaults otherwise. */
export const excursionPaxTargetForTrip = (
  isSic: boolean,
  pax: Step1ExcursionPax,
): { adultCount: number; childCount: number } =>
  isSic ? SIC_EXCURSION_PAX : defaultExcursionPaxCounts(pax)

/** Excursion or add-on row with client-side ticket count snapshots. */
export type TicketedExcursionRow = {
  tickets?: JourneyDayTripExcursionTicketItem[]
  _adult_category_id?: number
  _child_category_id?: number
  _adult_count?: number
  _child_count?: number
  _counts_manual?: boolean
  [key: string]: any
}

export const resolveAdultChildTickets = (
  tix: JourneyDayTripExcursionTicketItem[] | undefined,
) => {
  const list = Array.isArray(tix) ? tix : []
  const adultTix =
    list.find((t) => Number(t.category_id) === 1) ??
    list.find((t) => /adult/i.test(String(t.category_name || ""))) ??
    list[0]
  const childTix =
    list.find((t) => Number(t.category_id) === 2) ??
    list.find((t) => /child|kid/i.test(String(t.category_name || ""))) ??
    (list.length > 1 && list[1] !== adultTix ? list[1] : undefined)
  return { adultTix, childTix }
}

/** Apply adult/child counts (+ tickets) on one excursion or add-on row. */
export const applyPaxToTicketedRow = <TRow extends TicketedExcursionRow>(
  row: TRow,
  adultCount: number,
  childCount: number,
  buildTicketPair: BuildTicketPairFn,
  options?: { countsManual?: boolean },
): TRow => {
  const resolved = resolveAdultChildTickets(row.tickets)
  const adultCatId =
    row._adult_category_id ?? resolved.adultTix?.category_id ?? (adultCount > 0 ? 1 : undefined)
  const childCatId =
    row._child_category_id ?? resolved.childTix?.category_id ?? (childCount > 0 ? 2 : undefined)
  const next: TRow = {
    ...row,
    _adult_category_id: adultCatId,
    _child_category_id: childCatId,
    _adult_count: adultCount,
    _child_count: childCount,
    tickets: buildTicketPair(
      adultCatId,
      adultCount,
      childCatId,
      childCount,
      resolved.adultTix,
      resolved.childTix,
    ),
  }
  if (options?.countsManual !== undefined) {
    next._counts_manual = options.countsManual
  }
  return next
}

/** Zero or restore default pax on every excursion + add-on for one tour trip. */
export const applyTripExcursionPaxMode = (
  trip: JourneyDayTripItem,
  mode: "sic_zero" | "step1_default",
  pax: Step1ExcursionPax,
  buildTicketPair: BuildTicketPairFn,
): JourneyDayTripItem => {
  const { adultCount, childCount } =
    mode === "sic_zero" ? SIC_EXCURSION_PAX : defaultExcursionPaxCounts(pax)

  const excursions = (trip.excursions || []).map((exc) => {
    const addOns = ((exc as any).add_ons || []).map((ao: any) =>
      applyPaxToTicketedRow(ao, adultCount, childCount, buildTicketPair, { countsManual: false }),
    )
    return {
      ...applyPaxToTicketedRow(exc as any, adultCount, childCount, buildTicketPair, {
        countsManual: false,
      }),
      add_ons: addOns,
    } as JourneyDayTripExcursionItem
  })

  return { ...trip, excursions }
}
